import { useConfirm } from "material-ui-confirm";
import { AddressType } from "@googlemaps/google-maps-services-js";
import { FormikHelpers, useFormikContext } from "formik";
import { FormFieldError } from "./Form";
import {
  TextLabeledSegmentLabel,
  TextLabeledSegments,
  TextLabeledSegmentsInput,
} from "./TextLabeledSegments";
import { ValidatedFormikField } from "./formHelpers";
import { FormInputGroup, FormInputWithParse } from "./FormInputs";
import { cleanStateCode, renderAddressString } from "@portal/app/_common/lib/address";
import { cleanMaxLengthNumber } from "@portal/app/_common/lib/formatRenderers";
import { errorToast, infoToast } from "../context/ToastProvider";
import { Address } from "@portal/app/_common/types/address";
import { captureException } from "@sentry/nextjs";
import difference from "lodash/difference";
import { css, styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import Box from "@mui/material/Box";
import { DreamMuiLargeListItem } from "@portal/app/_components/ui-elements/Mui/DreamMuiLargeListItem";
import { GoogleMapsSinglePlaceEmbed } from "@portal/app/_components/ui-elements/GoogleMapsSinglePlaceEmbed";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleApiScript } from "@portal/app/_components/scripts/GoogleApiScript";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputBase from "@mui/material/InputBase";
import debounce from "lodash/debounce";

interface GoogleAddressAutocompleteProps {
  fieldNamespace?: string;
  requireStreetNumber?: boolean;
  appearance?: "default" | "hosted";
}

export const FIELDS = {
  street: "street",
  street2: "street2",
  city: "city",
  state: "state",
  zip: "zip",
  place: "place",
  placeId: "placeId",
} as const;

const transformAddressComponentsToAddress = (
  addressComponents?: google.maps.places.AddressComponent[]
): Required<Omit<Address, "street2">> & { streetNumber?: string } => {
  const getAddressPart = (type: string) =>
    addressComponents?.find((component) => component.types[0] === type);
  const streetNumber = getAddressPart("street_number")?.longText ?? undefined;
  const street = getAddressPart("route")?.longText ?? "";
  const city =
    getAddressPart("locality")?.longText ?? getAddressPart("sublocality_level_1")?.longText ?? "";
  const state = getAddressPart("administrative_area_level_1")?.shortText ?? "";
  const zip = getAddressPart("postal_code")?.longText ?? "";
  return {
    streetNumber,
    street: streetNumber ? `${streetNumber} ${street}`.trim() : "",
    city,
    state,
    zip,
  };
};

const apiKey = process.env.GOOGLE_CLOUD_API_KEY as string;

export const GoogleAddressAutocomplete = (props: GoogleAddressAutocompleteProps) => {
  const fieldKeys = useMemo(
    () =>
      Object.keys(FIELDS).reduce(
        (acc, field) => ({
          ...acc,
          [field]: props.fieldNamespace ? `${props.fieldNamespace}.${field}` : field,
        }),
        FIELDS
      ),
    [props.fieldNamespace]
  );
  const { setValues, getFieldProps } = useFormikContext<Record<string, string>>();
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>();
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const autocompleteRef = useRef<HTMLElement | null>(null);
  const latestAutocompleteRequestRef = useRef(0);
  const [open, setOpen] = useState(false);

  const fetchAutocomplete = useMemo(
    () =>
      debounce(async (newValue: string, requestId: number) => {
        if (requestId !== latestAutocompleteRequestRef.current) return;

        const response =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: newValue,
            includedRegionCodes: ["US"],
            includedPrimaryTypes: ["street_address", "geocode"],
            region: "US",
            sessionToken,
          });
        if (requestId !== latestAutocompleteRequestRef.current) return;

        setSuggestions(response.suggestions);
        setOpen(true);
      }, 300),
    [sessionToken]
  );

  useEffect(
    () => () => {
      fetchAutocomplete.cancel();
      latestAutocompleteRequestRef.current++;
    },
    [fetchAutocomplete]
  );

  const closeAutocomplete = useCallback(() => {
    latestAutocompleteRequestRef.current++;
    fetchAutocomplete.cancel();
    setSuggestions([]);
    setOpen(false);
  }, [fetchAutocomplete]);

  const onPlaceSelected = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      closeAutocomplete();
      const place = suggestion.placePrediction!.toPlace();
      const placeId = suggestion.placePrediction!.placeId ?? "";
      await place.fetchFields({ fields: ["addressComponents"] });
      const address = transformAddressComponentsToAddress(place.addressComponents);

      if (props.requireStreetNumber && !address.streetNumber) {
        infoToast("A street number is required!");
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await setValues((prevValues: any) => ({
        ...prevValues,
        ...(props.fieldNamespace
          ? {
              [props.fieldNamespace]: {
                ...prevValues[props.fieldNamespace],
                ...address,
                place,
                placeId,
              },
            }
          : {
              ...address,
              place,
              placeId,
            }),
      }));
    },
    [closeAutocomplete, setValues, props.fieldNamespace, props.requireStreetNumber]
  );

  const streetFormikField = getFieldProps(fieldKeys.street);
  const [internalStreetValue, setInternalStreetValue] = useState(streetFormikField.value);
  const isHosted = props.appearance === "hosted";

  return (
    <AddressFormInputGroup $appearance={props.appearance ?? "default"}>
      <TextLabeledSegments className={isHosted ? "GoogleAddressAutocomplete-segments" : undefined}>
        <Autocomplete<google.maps.places.AutocompleteSuggestion, false, true>
          className={isHosted ? "GoogleAddressAutocomplete-autocomplete" : undefined}
          options={suggestions}
          clearOnBlur={false}
          openOnFocus={false}
          disableClearable
          popupIcon={<></>}
          noOptionsText={null}
          fullWidth
          open={open}
          onClose={closeAutocomplete}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            if (event.nativeEvent.isComposing || event.which === 229) {
              // Let the IME handle Escape without dismissing the suggestions.
              event.defaultMuiPrevented = true;
              return;
            }
            closeAutocomplete();
          }}
          ref={autocompleteRef}
          filterOptions={(x) => x}
          inputValue={internalStreetValue}
          getOptionLabel={(suggestion) => suggestion.placePrediction?.mainText?.text ?? ""}
          renderOption={(props, suggestion) => {
            const { text = "", matches = [] } = suggestion.placePrediction?.text ?? {};
            const highlightedText = matches.reduce((acc, { startOffset, endOffset }, i, arr) => {
              /**
               * This captures any un-highlighted text before or between highlighted text
               */
              const prevMatch = arr[i - 1];
              if (prevMatch || (!prevMatch && startOffset > 0)) {
                const subText = text.slice(prevMatch ? prevMatch.endOffset : 0, startOffset);
                acc.push(<span key={`${i}-before`}>{subText}</span>);
              }
              /**
               * This captures each section of highlighted text
               */
              const subText = text.slice(startOffset, endOffset);
              acc.push(
                <span style={{ fontWeight: "bold" }} key={`${i}-match`}>
                  {subText}
                </span>
              );
              /**
               * This captures the remaining un-highlighted text
               */
              if (!arr[i + 1]) {
                const subText = text.slice(endOffset);
                acc.push(<span key={`${i}-after`}>{subText}</span>);
              }
              return acc;
            }, [] as React.ReactNode[]);
            return (
              <li {...props} key={text} data-testid={text}>
                <span>{highlightedText}</span>
              </li>
            );
          }}
          /** When the user selects an option **/
          onChange={(_, selectedSuggestion) => onPlaceSelected(selectedSuggestion)}
          /** When the user types in the input **/
          onInputChange={(_, searchValue, reason) => {
            setInternalStreetValue(searchValue);
            if (reason !== "input" || !searchValue.trim()) {
              closeAutocomplete();
              return;
            }
            const requestId = ++latestAutocompleteRequestRef.current;
            return fetchAutocomplete(searchValue, requestId);
          }}
          renderInput={(params) => (
            <>
              {isHosted && (
                <label className="GoogleAddressAutocomplete-label" htmlFor={params.id}>
                  Street address
                </label>
              )}
              <TextField
                {...params}
                helperText={isHosted ? undefined : "Street"}
                style={{ width: "100%", fontSize: "1.1rem" }}
                slots={{
                  input: StyledInputBase,
                  formHelperText: TextLabeledSegmentLabel,
                }}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                      params.inputProps.onBlur?.(event);
                      closeAutocomplete();
                    },
                    "data-testid": "street",
                    autoComplete: "address-line1",
                  },
                }}
              />
            </>
          )}
        />
        {streetFormikField.value && (
          <ValidatedFormikField
            as={TextLabeledSegmentsInput}
            $inputAs={FormInputWithParse}
            wrapperClassName={isHosted ? "GoogleAddressAutocomplete-field" : undefined}
            name={fieldKeys.street2}
            style={{ width: 75 }}
            autoFocus
            label="Apt, suite, or unit"
            autoComplete="address-line2"
            data-testid="street2"
          />
        )}
      </TextLabeledSegments>
      {streetFormikField.value && (
        <TextLabeledSegments
          className={isHosted ? "GoogleAddressAutocomplete-segments" : undefined}
        >
          <ValidatedFormikField
            as={TextLabeledSegmentsInput}
            $inputAs={FormInputWithParse}
            wrapperClassName={
              isHosted
                ? "GoogleAddressAutocomplete-field GoogleAddressAutocomplete-city"
                : "GoogleAddressAutocomplete-city"
            }
            name={fieldKeys.city}
            label="City"
            autoComplete="address-level2"
            data-testid="city"
          />
          <ValidatedFormikField
            as={TextLabeledSegmentsInput}
            $inputAs={FormInputWithParse}
            wrapperClassName={
              isHosted
                ? "GoogleAddressAutocomplete-field GoogleAddressAutocomplete-state"
                : "GoogleAddressAutocomplete-state"
            }
            $fromDisplay={cleanStateCode}
            $toDisplay={cleanStateCode}
            name={fieldKeys.state}
            label="State"
            autoComplete="address-level1"
            data-testid="state"
          />
          <ValidatedFormikField
            as={TextLabeledSegmentsInput}
            $inputAs={FormInputWithParse}
            wrapperClassName={
              isHosted
                ? "GoogleAddressAutocomplete-field GoogleAddressAutocomplete-zip"
                : "GoogleAddressAutocomplete-zip"
            }
            $fromDisplay={cleanMaxLengthNumber(5)}
            $toDisplay={cleanMaxLengthNumber(5)}
            name={fieldKeys.zip}
            label="ZIP code"
            autoComplete="postal-code"
            inputMode="numeric"
            data-testid="zip"
          />
        </TextLabeledSegments>
      )}
      <FormFieldError name={fieldKeys.street} />
      <FormFieldError name={fieldKeys.street2} />
      <FormFieldError name={fieldKeys.city} />
      <FormFieldError name={fieldKeys.state} />
      <FormFieldError name={fieldKeys.zip} />
      <GoogleApiScript
        onReady={() =>
          google.maps
            .importLibrary("places")
            .then(() => setSessionToken(new google.maps.places.AutocompleteSessionToken()))
        }
      />
    </AddressFormInputGroup>
  );
};

const AddressFormInputGroup = styled(FormInputGroup, {
  shouldForwardProp: (prop) => prop !== "$appearance",
})<{ $appearance: "default" | "hosted" }>`
  & .GoogleAddressAutocomplete-city {
    width: 9.375rem;
  }

  & .GoogleAddressAutocomplete-state {
    width: 3.125rem;
  }

  & .GoogleAddressAutocomplete-zip {
    width: 4.375rem;
  }

  ${({ $appearance, theme }) =>
    $appearance === "hosted" &&
    css`
      padding: 0;
      border: 0;
      box-shadow: none;
      font-size: 1rem;

      & .GoogleAddressAutocomplete-segments {
        gap: 12px;
        margin: 0 0 16px;
      }

      & .GoogleAddressAutocomplete-segments > * {
        margin: 0;
      }

      & .GoogleAddressAutocomplete-city {
        flex-basis: 0;
      }

      & .GoogleAddressAutocomplete-state {
        width: 5rem;
        flex-grow: 0;
        flex-shrink: 0;
      }

      & .GoogleAddressAutocomplete-zip {
        width: 100%;
        flex-grow: 0;
        flex-shrink: 0;
      }

      & .MuiFormControl-root,
      & .GoogleAddressAutocomplete-field {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      & .MuiFormHelperText-root,
      & .GoogleAddressAutocomplete-label,
      & .GoogleAddressAutocomplete-field > .label {
        display: block;
        margin: 0 0 8px;
        padding: 0;
        border: 0;
        color: ${theme.palette.text.primary};
        font-size: 0.95rem;
        font-weight: 700;
        line-height: 1.4;
        text-transform: none;
      }

      & .GoogleAddressAutocomplete-field > .label {
        order: -1;
      }

      & .GoogleAddressAutocomplete-autocomplete {
        width: 100%;
      }

      & .GoogleAddressAutocomplete-autocomplete .MuiInputBase-root {
        min-height: 56px;
        padding: 0 16px;
        border: 1px solid ${theme.palette.dream.borderColor};
        border-radius: 8px;
        background: ${theme.palette.common.white};
      }

      & .GoogleAddressAutocomplete-autocomplete .MuiInputBase-root:hover {
        border-color: ${theme.palette.primary.main};
      }

      & .GoogleAddressAutocomplete-autocomplete .MuiInputBase-root.Mui-focused {
        border-width: 2px;
        border-color: ${theme.palette.primary.main};
      }

      & .GoogleAddressAutocomplete-autocomplete input {
        min-height: 54px;
        /* MUI Autocomplete adds input padding at a more specific selector. */
        padding: 0 !important;
        border: 0;
        font-size: 1.1rem;
      }

      & .GoogleAddressAutocomplete-field > input {
        order: 1;
        box-sizing: border-box;
        width: 100%;
        height: 56px;
        padding: 0 16px;
        border: 1px solid ${theme.palette.dream.borderColor};
        border-radius: 8px;
        background: ${theme.palette.common.white};
        font-size: 1.1rem;
      }

      & .GoogleAddressAutocomplete-field > input:hover {
        border-color: ${theme.palette.primary.main};
      }

      & .GoogleAddressAutocomplete-field > input:focus {
        border-width: 2px;
        border-color: ${theme.palette.primary.main};
        outline: none;
      }

      & .GoogleAddressAutocomplete-field > input.-hasError {
        border-color: ${theme.palette.error.main};
      }
    `}
`;

export const useFormikAddressCheckOnBeforeSubmit = (props?: {
  fieldNamespace?: string;
}): {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBeforeSubmit: (values: any, formik: FormikHelpers<any>) => boolean | Promise<boolean>;
} => {
  const { isMobile } = usePageContext();
  const confirm = useConfirm();
  return {
    onBeforeSubmit: (values, formik) =>
      new Promise(async (resolve) => {
        try {
          const addressValues = props?.fieldNamespace ? values[props.fieldNamespace] : values;
          const place = addressValues[FIELDS.place] as google.maps.places.Place | undefined;
          const addressFromPlace = transformAddressComponentsToAddress(place?.addressComponents);
          const valuesStreet = addressValues[FIELDS.street];
          const valuesStreet2 = addressValues[FIELDS.street2];
          const valuesCity = addressValues[FIELDS.city];
          const valuesState = addressValues[FIELDS.state];
          const valuesZip = addressValues[FIELDS.zip];

          if (!valuesStreet && !valuesCity && !valuesState) {
            return resolve(true);
          }

          /**
           * If the address is the same as the one we're trying to validate,
           * then we can skip the validation.
           */
          if (
            addressFromPlace.street === valuesStreet &&
            addressFromPlace.city === valuesCity &&
            addressFromPlace.state === valuesState &&
            addressFromPlace.zip === valuesZip
          ) {
            return resolve(true);
          }
          const response = await fetch(
            `https://addressvalidation.googleapis.com/v1:validateAddress?key=${apiKey}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                address: {
                  regionCode: "US",
                  addressLines: [
                    valuesStreet,
                    ...(valuesStreet2 ? [valuesStreet2] : []),
                    valuesCity,
                    valuesState,
                    valuesZip,
                  ],
                },
              }),
            }
          );
          if (!response.ok) throw new Error(`Address validation failed: ${response.status}`);
          const responseData = await response.json();
          const result = responseData.result as {
            geocode?: { placeId?: string; placeTypes?: AddressType[] };
            address?: {
              addressComponents?: {
                componentType?: string;
                componentName?: { text: string };
              }[];
            };
            metadata?: {
              residential?: boolean;
              business?: boolean;
              poBox?: boolean;
            };
          };
          const addressComponents = result.address?.addressComponents;
          const streetNumber = addressComponents?.find(
            ({ componentType }) => componentType === "street_number"
          )?.componentName?.text;
          const streetWithoutNumber = addressComponents?.find(
            ({ componentType }) => componentType === "route"
          )?.componentName?.text;
          const street = `${streetNumber} ${streetWithoutNumber}`.trim();
          const street2 = addressComponents?.find(
            ({ componentType }) => componentType === "subpremise"
          )?.componentName?.text;
          const city = (
            addressComponents?.find(
              ({ componentType }) => componentType === "sublocality_level_1"
            ) ?? addressComponents?.find(({ componentType }) => componentType === "locality")
          )?.componentName?.text;
          const state = addressComponents?.find(
            ({ componentType }) => componentType === "administrative_area_level_1"
          )?.componentName?.text;
          const zip = addressComponents?.find(
            ({ componentType }) => componentType === "postal_code"
          )?.componentName?.text;
          const placeId = result.geocode?.placeId;
          const differences = difference(
            [street, street2 || undefined, city, state, zip],
            [valuesStreet, valuesStreet2 || undefined, valuesCity, valuesState, valuesZip]
          );
          const streetChanged = street !== valuesStreet;
          const street2Changed = street2 !== valuesStreet2;
          const cityChanged = city !== valuesCity;
          const stateChanged = state !== valuesState;
          const zipChanged = zip !== valuesZip;
          if (differences.length > 0) {
            await confirm({
              title: "Can we fix your address?",
              confirmationText: "Yes, change",
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              confirmationButtonProps: { "data-testid": "confirm-change" },
              cancellationText: "Don't change",
              cancellationButtonProps: {
                variant: "text",
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                "data-testid": "dont-change",
              },
              dialogProps: { fullScreen: isMobile },
              dialogActionsProps: {
                sx: {
                  justifyContent: "space-between",
                  padding: isMobile ? "1.5rem" : "2rem",
                },
              },
              allowClose: false,
              content: (
                <Box>
                  <DreamMuiLargeListItem
                    title="What you entered:"
                    data-testid="entered-address"
                    variant="clickable"
                  >
                    <AddressPart component="span" $changed={streetChanged}>
                      {valuesStreet}
                    </AddressPart>
                    {valuesStreet2 ? (
                      <>
                        <br />
                        <AddressPart component="span" $changed={street2Changed}>
                          {valuesStreet2}
                        </AddressPart>
                      </>
                    ) : null}
                    <br />
                    <AddressPart component="span" $changed={cityChanged}>
                      {valuesCity}
                    </AddressPart>
                    ,{" "}
                    <AddressPart component="span" $changed={stateChanged}>
                      {valuesState}
                    </AddressPart>{" "}
                    <AddressPart component="span" $changed={zipChanged}>
                      {valuesZip}
                    </AddressPart>
                  </DreamMuiLargeListItem>
                  <DreamMuiLargeListItem
                    title="Recommended:"
                    data-testid="recommended-address"
                    variant="clickable"
                  >
                    <Typography>
                      <AddressPart component="span" $changed={streetChanged}>
                        {street}
                      </AddressPart>
                      {street2 ? (
                        <>
                          <br />
                          <AddressPart component="span" $changed={street2Changed}>
                            {street2}
                          </AddressPart>
                        </>
                      ) : null}
                      <br />
                      <AddressPart component="span" $changed={cityChanged}>
                        {city}
                      </AddressPart>
                      ,{" "}
                      <AddressPart component="span" $changed={stateChanged}>
                        {state}
                      </AddressPart>{" "}
                      <AddressPart component="span" $changed={zipChanged}>
                        {zip}
                      </AddressPart>
                    </Typography>
                    <Box marginTop="1rem">
                      <GoogleMapsSinglePlaceEmbed
                        placeId={placeId}
                        address={renderAddressString({
                          street,
                          street2,
                          city,
                          state,
                          zip,
                        })}
                        zoom={15}
                      />
                    </Box>
                  </DreamMuiLargeListItem>
                </Box>
              ),
            })
              .then(async () => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await formik.setValues((prevValues: any) => ({
                  ...prevValues,
                  ...(props?.fieldNamespace
                    ? {
                        [props.fieldNamespace]: {
                          ...prevValues[props.fieldNamespace],
                          street,
                          street2,
                          city,
                          state,
                          zip,
                          place,
                          placeId,
                        },
                      }
                    : {
                        street,
                        street2,
                        city,
                        state,
                        zip,
                        place,
                        placeId,
                      }),
                }));
                /**
                 * Potential gotcha-- there's no way to update the values and
                 * let it continue. So update the values, cancel the current
                 * submission, and then submit again. This will also allow us
                 * to rerun validation on the corrected values.
                 */
                setTimeout(() => {
                  resolve(false);
                  void formik.submitForm();
                }, 200);
              })
              /**
               * If they hit "Keep original", then continue submitting
               */
              .catch(() => resolve(true));
          } else {
            resolve(true);
          }
        } catch (error) {
          captureException(error, { contexts: { values } });
          errorToast("Please enter a valid address.");
          return resolve(false);
        }
      }),
  };
};

const AddressPart = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$changed",
})<{
  component: React.ElementType;
  $changed: boolean;
}>`
  ${({ $changed }) => $changed && { background: "#d3e4fc" }}
`;

export const StyledAutocomplete = styled(Autocomplete)`
  margin-right: 10px;
  margin-bottom: 10px;

  display: flex;
  flex-direction: column;
  align-items: stretch;

  @media (max-width: 450px) {
    flex-grow: 1;
  }
` as typeof Autocomplete;

export const StyledInputBase = styled(InputBase)`
  &.MuiInputBase-root {
    border: none;
    border-radius: 0;
    padding: 0;

    &&&:focus {
      box-shadow: none;
    }

    &&&:disabled {
      background: none;
    }

    & + .label {
      transition: 100ms linear all;
    }

    &:hover + .label {
      border-top-color: ${({ theme }) => theme.palette.primary.main};
    }

    &:focus + .label {
      border-top-color: ${({ theme }) => theme.palette.primary.main};
      font-weight: 700;
    }

    &.-hasError + .label {
      border-top-color: ${({ theme }) => theme.palette.error.main} !important;
      color: ${({ theme }) => theme.palette.error.main} !important;
    }
  }
`;
